import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'
import { useWorkspaceStore } from '@/stores/workspace'
import { useAuthStore } from '@/stores/auth'
import { normalizeError } from './errors'

/**
 * Single axios instance used by every `/api/*` request.
 *
 * Critical rules (architecture §3.1, 4_CRITICAL_RULES.md #1):
 *   1. Every request MUST include `X-Workspace-ID` reflecting the active
 *      workspace context. The request interceptor below is the ONLY
 *      insertion point — never bypass it (PAIN_POINTS #18).
 *   2. On 401 we transparently attempt `POST /api/auth/refresh` exactly
 *      once (singleflight — concurrent 401s share the same promise) and
 *      retry the original request. Refresh failure → logout + redirect.
 *   3. All errors are normalized to `AppError` via `normalizeError` so
 *      callers don't introspect raw axios errors.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true, // sends httpOnly auth cookie
  timeout: 30_000,
})

/**
 * Admin endpoints live at `/admin/*` (NOT `/api/admin/*`) to bypass the
 * Go body-limit middleware mounted on `/api/*`. Decision: D-04. We pay
 * the small cost of a second axios instance to avoid Go-side churn on
 * 50+ admin handlers.
 */
export const adminClient: AxiosInstance = axios.create({
  baseURL: '/admin',
  withCredentials: true,
  timeout: 30_000,
})

attachInterceptors(apiClient)
attachInterceptors(adminClient)

// ---------------------------------------------------------------------------
// Interceptor wiring
// ---------------------------------------------------------------------------

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

/**
 * Singleflight for refresh: all concurrent 401 responses await the same
 * `/api/auth/refresh` promise (R-E2-3). Cleared once it settles.
 */
let refreshInFlight: Promise<unknown> | null = null

function attachInterceptors(client: AxiosInstance): void {
  // Request: stamp X-Workspace-ID when an active workspace is selected.
  client.interceptors.request.use((config) => {
    const wsId = useWorkspaceStore.getState().activeWsId
    if (wsId !== null) {
      config.headers.set('X-Workspace-ID', String(wsId))
    }
    return config
  })

  // Response: 401 refresh-retry, then logout+redirect; normalize all errors.
  client.interceptors.response.use(
    (resp) => resp,
    async (err: AxiosError) => {
      const status = err.response?.status
      const original = err.config as RetryableConfig | undefined

      if (status === 401 && original && !original._retry) {
        original._retry = true
        const reqUrl = original.url ?? ''
        // Skip refresh-retry for the auth endpoints themselves. The original
        // gate was just `/auth/refresh`, which left `/auth/login` retrying
        // through refresh — a wrong-password 401 would silently refresh
        // (succeeds because the user's cookie from a prior session is still
        // valid) and re-submit the same wrong credentials, charging two
        // attempts against the 3/5-min rate limit per real click. Same logic
        // for register / forgot-password: those endpoints gate on
        // credentials, not session state, so refreshing is a no-op at best
        // and a rate-limit doubler at worst.
        const isPreAuthEndpoint =
          reqUrl.endsWith('/auth/refresh') ||
          reqUrl.endsWith('/auth/login') ||
          reqUrl.endsWith('/auth/register') ||
          reqUrl.endsWith('/auth/forgot-password')
        if (!isPreAuthEndpoint) {
          try {
            if (!refreshInFlight) {
              refreshInFlight = apiClient
                .post('/auth/refresh')
                .finally(() => {
                  refreshInFlight = null
                })
            }
            await refreshInFlight
            return client(original)
          } catch {
            // fall through to logout
          }
        }
      }

      if (status === 401) {
        // Don't bounce the user to /login when they are ALREADY on a
        // public auth page. Pre-auth probes (e.g. GET /api/auth/check-
        // username from the register form) come back as 401 because
        // the Go-side auth middleware does not whitelist them; that's
        // the auth flow's own problem to surface, not a "session
        // expired" signal. Without this guard, typing a username on
        // /register triggers a 401 → refresh → 403 → redirect-to-login
        // loop and the user can never finish registering.
        const onPublicAuthPage =
          typeof window !== 'undefined' &&
          (window.location.pathname.startsWith('/login') ||
            window.location.pathname.startsWith('/register') ||
            window.location.pathname.startsWith('/forgot-password') ||
            window.location.pathname.startsWith('/accept-invite'))
        if (!onPublicAuthPage) {
          useAuthStore.getState().logout()
          if (typeof window !== 'undefined') {
            const returnTo = encodeURIComponent(
              window.location.pathname + window.location.search,
            )
            window.location.assign(`/login?returnTo=${returnTo}`)
          }
        }
      }

      return Promise.reject(normalizeError(err))
    },
  )
}

// ---------------------------------------------------------------------------
// buildDirectUrl — for browser tag requests that can't carry custom headers
// ---------------------------------------------------------------------------

/**
 * Build a URL for direct browser navigation (`<a href>`, `<video src>`,
 * `<img src>`) that needs workspace authorization. The Go `StorageAuth`
 * middleware accepts `?ws=N` precisely because browser tag requests do
 * not carry custom headers (architecture §3.1; PAIN_POINTS #8).
 *
 * Never use this for blob/fetch downloads — those go through `apiClient`
 * which sets the header.
 */
export function buildDirectUrl(
  path: string,
  extra?: Record<string, string>,
): string {
  const wsId = useWorkspaceStore.getState().activeWsId
  // SSR-safe origin fallback so the helper is callable in Node tests too.
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  const url = new URL(path, origin)
  if (wsId !== null) {
    url.searchParams.set('ws', String(wsId))
  }
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      url.searchParams.set(k, v)
    }
  }
  return url.pathname + url.search
}

// Re-export error helpers so callers can `import { ..., AppError } from '@/api/client'`
// without hopping to a second module.
export {
  normalizeError,
  isAuthError,
  isValidationError,
  isNetworkError,
} from './errors'
export type { AppError } from './errors'
